import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { useUsuarios, useEmpresas } from "@/hooks/use-data";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Mail,
  Phone,
  UserPlus,
  X,
  Loader2,
  Pencil,
  Lock,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  KeyRound,
  Eye,
  EyeOff,
  Search,
  FileText,
  Plus,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useState, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { api } from "@shared/routes";
import { isCurrentArt } from '@shared/art-validity';
import type { UserProfile } from "@shared/schema";
import {
  formatCpf,
  formatPhone,
  formatCep,
  unformatCep,
  unformatCpf,
  unformatPhone,
} from "@/lib/formatters";
import { ROLE_LABELS, ROLE_BADGE_COLORS, ALL_ROLES } from "@/lib/constants";
import { AlterarSenhaDialog } from "@/components/usuarios/AlterarSenhaDialog";
import { CertificadoDigital } from "@/components/usuarios/CertificadoDigital";

const ESTADOS_BR = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

// ─── Shared form fields ────────────────────────────────────────────────────────

interface UserFormState {
  nome: string;
  telefone: string;
  whatsapp: string;
  cpf: string;
  crea: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

function initialFormState(user?: UserProfile): UserFormState {
  return {
    nome: user?.nome ?? "",
    telefone: user?.telefone ? formatPhone(user.telefone) : "",
    whatsapp: user?.whatsapp ? formatPhone(user.whatsapp) : "",
    cpf: user?.cpf ? formatCpf(user.cpf) : "",
    crea: user?.crea ?? "",
    logradouro: user?.logradouro ?? "",
    numero: user?.numero ?? "",
    complemento: user?.complemento ?? "",
    bairro: user?.bairro ?? "",
    cidade: user?.cidade ?? "",
    estado: user?.estado ?? "",
    cep: user?.cep ? formatCep(user.cep) : "",
  };
}

function AdditionalFields({
  form,
  onChange,
  selectedRoles,
}: {
  form: UserFormState;
  onChange: (key: keyof UserFormState, value: string) => void;
  selectedRoles: string[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <span>Detalhes</span>
        {open ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>
      {open && (
        <div className="p-4 grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="af-whatsapp">WhatsApp</Label>
            <Input
              id="af-whatsapp"
              value={form.whatsapp}
              onChange={(e) =>
                onChange("whatsapp", formatPhone(e.target.value))
              }
              placeholder="(00) 00000-0000"
              maxLength={15}
              inputMode="numeric"
            />
          </div>
          {selectedRoles.includes("rt") && (
            <div className="space-y-1.5">
              <Label htmlFor="af-crea">CREA</Label>
              <Input
                id="af-crea"
                value={form.crea}
                onChange={(e) => onChange("crea", e.target.value)}
                placeholder="CREA-UF 000000"
              />
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="af-logradouro">Logradouro</Label>
            <Input
              id="af-logradouro"
              value={form.logradouro}
              onChange={(e) => onChange("logradouro", e.target.value)}
              placeholder="Rua, Av., etc."
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-numero">Número</Label>
            <Input
              id="af-numero"
              value={form.numero}
              onChange={(e) => onChange("numero", e.target.value)}
              placeholder="123"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-complemento">Complemento</Label>
            <Input
              id="af-complemento"
              value={form.complemento}
              onChange={(e) => onChange("complemento", e.target.value)}
              placeholder="Apto, Sala…"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-bairro">Bairro</Label>
            <Input
              id="af-bairro"
              value={form.bairro}
              onChange={(e) => onChange("bairro", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-cidade">Cidade</Label>
            <Input
              id="af-cidade"
              value={form.cidade}
              onChange={(e) => onChange("cidade", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Estado</Label>
            <Select
              value={form.estado || "__none__"}
              onValueChange={(v) =>
                onChange("estado", v === "__none__" ? "" : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="UF" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Selecione —</SelectItem>
                {ESTADOS_BR.map((uf) => (
                  <SelectItem key={uf} value={uf}>
                    {uf}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-cep">CEP</Label>
            <Input
              id="af-cep"
              value={form.cep}
              onChange={(e) => onChange("cep", formatCep(e.target.value))}
              placeholder="00000-000"
              maxLength={9}
              inputMode="numeric"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CreateUsuarioForm ─────────────────────────────────────────────────────────

function CreateUsuarioForm({
  onClose,
  isOwner,
  isAdmin,
  fixedEmpresaId,
}: {
  onClose: () => void;
  isOwner: boolean;
  isAdmin: boolean;
  fixedEmpresaId?: string;
}) {
  const { session } = useAuth();
  const { toast } = useToast();
  const { data: empresas } = useEmpresas();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [empresaId, setEmpresaId] = useState(fixedEmpresaId ?? "");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const selectedEmpresaTipo = empresas?.find((e) => e.id === empresaId)?.tipo;
  const isParceiro = selectedEmpresaTipo === "PARCEIRO_DE_NEGOCIOS";
  const rolesDisponiveis = ALL_ROLES.filter((role) => {
    if (role === "owner") return isOwner;
    if (isParceiro) return role === "parceiro_negocios";
    return role !== "parceiro_negocios";
  });
  const [telefone, setTelefone] = useState("");
  const [form, setForm] = useState<UserFormState>(initialFormState());

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch(api.usuarios.create.path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar usuário");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.usuarios.list.path] });
      toast({ title: "Usuário criado com sucesso." });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function toggleRole(role: string) {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate({
      nome: form.nome,
      email,
      password,
      empresaId,
      roles: selectedRoles,
      telefone: telefone ? unformatPhone(telefone) : undefined,
      whatsapp: form.whatsapp ? unformatPhone(form.whatsapp) : undefined,
      cpf: form.cpf ? unformatCpf(form.cpf) : undefined,
      crea: form.crea || undefined,
      logradouro: form.logradouro || undefined,
      numero: form.numero || undefined,
      complemento: form.complemento || undefined,
      bairro: form.bairro || undefined,
      cidade: form.cidade || undefined,
      estado: form.estado || undefined,
      cep: form.cep ? unformatCep(form.cep) : undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="u-nome">Nome *</Label>
          <Input
            id="u-nome"
            value={form.nome}
            onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            placeholder="Nome completo"
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="u-email">E-mail *</Label>
          <Input
            id="u-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="usuario@email.com"
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="u-password">Senha *</Label>
          <Input
            id="u-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mínimo 6 caracteres"
            required
            minLength={6}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="u-telefone">Telefone</Label>
          <Input
            id="u-telefone"
            value={telefone}
            onChange={(e) => setTelefone(formatPhone(e.target.value))}
            placeholder="(00) 00000-0000"
            maxLength={15}
            inputMode="numeric"
          />
        </div>
      </div>

      {isOwner || isAdmin ? (
        <div className="space-y-1.5">
          <Label>Empresa *</Label>
          <Select
            value={empresaId}
            onValueChange={(id) => {
              setEmpresaId(id);
              setSelectedRoles([]);
            }}
            required
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a empresa" />
            </SelectTrigger>
            <SelectContent>
              {empresas?.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.nomeFantasia || emp.razaoSocial}{" "}
                  <span className="text-xs text-muted-foreground ml-1">
                    ({emp.tipo})
                  </span>
                  {["OWNER", "VISTORIADORA"].filter((e) => e != emp.tipo) && (
                    <span className="text-xs text-muted-foreground ml-1">
                      ({emp.idProprio ?? "Sem ID Próprio"})
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label>Empresa</Label>
          <p className="text-sm text-slate-600 px-3 py-2 bg-slate-50 rounded-md border">
            {empresas?.find((e) => e.id === empresaId)?.nomeFantasia ||
              empresas?.find((e) => e.id === empresaId)?.razaoSocial ||
              "—"}
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <Label>Perfis *</Label>
        <div className="flex flex-wrap gap-2">
          {rolesDisponiveis.map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => toggleRole(role)}
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                selectedRoles.includes(role)
                  ? cn(ROLE_BADGE_COLORS[role], "border-current")
                  : "bg-white text-slate-500 border-slate-200 hover:border-slate-400",
              )}
            >
              {ROLE_LABELS[role]}
            </button>
          ))}
        </div>
        {selectedRoles.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Selecione ao menos um perfil.
          </p>
        )}
      </div>

          <div className="space-y-1.5">
            <Label htmlFor="u-cpf">CPF *</Label>
            <Input
              id="u-cpf"
              value={form.cpf}
              onChange={(e) => setForm((f) => ({ ...f, cpf: formatCpf(e.target.value) }))}
              placeholder="000.000.000-00"
              maxLength={14}
              required
              inputMode="numeric"
            />
          </div>

      <AdditionalFields
        form={form}
        selectedRoles={selectedRoles}
        onChange={(key, val) => setForm((f) => ({ ...f, [key]: val }))}
      />

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={
            mutation.isPending ||
            !form.nome ||
            !email ||
            !password ||
            !empresaId ||
            selectedRoles.length === 0
          }
        >
          {mutation.isPending && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          Criar usuário
        </Button>
      </div>
    </form>
  );
}

// ─── EditUsuarioForm ───────────────────────────────────────────────────────────

function EditUsuarioForm({
  user,
  onClose,
  isOwner,
}: {
  user: UserProfile;
  onClose: () => void;
  isOwner: boolean;
}) {
  const { session } = useAuth();
  const { toast } = useToast();

  const [selectedRoles, setSelectedRoles] = useState<string[]>(user.roles);
  const [telefone, setTelefone] = useState(
    user.telefone ? formatPhone(user.telefone) : "",
  );
  const [form, setForm] = useState<UserFormState>(initialFormState(user));

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch(`${api.usuarios.list.path}/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao atualizar usuário");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.usuarios.list.path] });
      toast({ title: "Usuário atualizado com sucesso." });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function toggleRole(role: string) {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (selectedRoles.includes("rt")) {
      if (!form.crea.trim()) {
        toast({
          title: "CREA obrigatório",
          description: "Preencha o CREA antes de salvar o Responsável Técnico.",
          variant: "destructive",
        });
        return;
      }
    }
    mutation.mutate({
      nome: form.nome,
      roles: selectedRoles,
      telefone: telefone ? unformatPhone(telefone) : null,
      whatsapp: form.whatsapp ? unformatPhone(form.whatsapp) : null,
      cpf: form.cpf ? unformatCpf(form.cpf) : null,
      crea: form.crea || null,
      logradouro: form.logradouro || null,
      numero: form.numero || null,
      complemento: form.complemento || null,
      bairro: form.bairro || null,
      cidade: form.cidade || null,
      estado: form.estado || null,
      cep: form.cep ? unformatCep(form.cep) : null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="eu-nome">Nome *</Label>
          <Input
            id="eu-nome"
            value={form.nome}
            onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            placeholder="Nome completo"
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label>E-mail</Label>
          <p className="text-sm text-slate-600 px-3 py-2 bg-slate-50 rounded-md border truncate">
            {user.email}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="eu-telefone">Telefone</Label>
          <Input
            id="eu-telefone"
            value={telefone}
            onChange={(e) => setTelefone(formatPhone(e.target.value))}
            placeholder="(00) 00000-0000"
            maxLength={15}
            inputMode="numeric"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Perfis *</Label>
        <div className="flex flex-wrap gap-2">
          {ALL_ROLES.filter((role) => isOwner || role !== "owner").map(
            (role) => (
              <button
                key={role}
                type="button"
                onClick={() => toggleRole(role)}
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                  selectedRoles.includes(role)
                    ? cn(ROLE_BADGE_COLORS[role], "border-current")
                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-400",
                )}
              >
                {ROLE_LABELS[role]}
              </button>
            ),
          )}
        </div>
        {selectedRoles.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Selecione ao menos um perfil.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="edit-user-cpf">CPF do usuário</Label>
        <Input id="edit-user-cpf" value={form.cpf} onChange={(e) => setForm((f) => ({ ...f, cpf: formatCpf(e.target.value) }))} placeholder="000.000.000-00" maxLength={14} inputMode="numeric" />
      </div>

      <AdditionalFields
        form={form}
        selectedRoles={selectedRoles}
        onChange={(key, val) => setForm((f) => ({ ...f, [key]: val }))}
      />

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={
            mutation.isPending || !form.nome || selectedRoles.length === 0
          }
        >
          {mutation.isPending && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          Salvar alterações
        </Button>
      </div>
    </form>
  );
}

// ─── Certificado Digital RT ────────────────────────────────────────────────────

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function CertificadoDigitalCard({
  user,
  canManage,
  canView,
}: {
  user: UserProfile;
  canManage: boolean;
  canView: boolean;
}) {
  const { session } = useAuth();
  const { toast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [senha, setSenha] = useState("");
  const [senhaConfirm, setSenhaConfirm] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: certStatus, refetch: refetchCert, isLoading: certLoading } = useQuery({
    queryKey: [`rt-certificado-${user.id}`],
    queryFn: async () => {
      const res = await fetch(`/api/admin/rt-certificado/${user.id}/status`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) return { temCertificado: false, senhaConfigurada: false };
      return res.json() as Promise<{ temCertificado: boolean; senhaConfigurada: boolean; nomeArquivo?: string; criadoEm?: string }>;
    },
    enabled: canView && !!session,
  });

  function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setSenha("");
    setSenhaConfirm("");
    setShowModal(true);
    e.target.value = "";
  }

  async function handleSubmit() {
    if (!selectedFile || !senha) return;
    if (senha !== senhaConfirm) {
      toast({ title: "As senhas não conferem.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const buffer = await selectedFile.arrayBuffer();
      const base64 = arrayBufferToBase64(buffer);
      const res = await fetch(`/api/admin/rt-certificado/${user.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ certificadoBase64: base64, nomeArquivo: selectedFile.name, senha }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao salvar certificado");
      }
      setShowModal(false);
      setSenha("");
      setSenhaConfirm("");
      setSelectedFile(null);
      refetchCert();
      toast({ title: "Certificado digital cadastrado com sucesso." });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!confirm(`Remover o certificado digital de ${user.nome}? Esta ação não pode ser desfeita.`)) return;
    try {
      const res = await fetch(`/api/admin/rt-certificado/${user.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message);
      }
      refetchCert();
      toast({ title: "Certificado removido." });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    }
  }

  function closeModal() {
    if (submitting) return;
    setShowModal(false);
    setSenha("");
    setSenhaConfirm("");
    setSelectedFile(null);
  }

  if (!canView) return null;

  return (
    <div className="mt-2 pt-2 border-t">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs">
          <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-500">Certificado Digital</span>
          {certLoading ? (
            <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
          ) : certStatus?.temCertificado ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              <span className="text-green-600 font-medium">Associado</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-amber-600">Não configurado</span>
            </>
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-1">
            {certStatus?.temCertificado && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={handleDelete}
              >
                Remover
              </Button>
            )}
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".pfx,.p12"
                className="hidden"
                onChange={handleFileSelected}
              />
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-xs px-2 pointer-events-none"
                asChild={false}
              >
                {certStatus?.temCertificado ? "Atualizar" : "Cadastrar"}
              </Button>
            </label>
          </div>
        )}
      </div>

      {certStatus?.temCertificado && (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
          <KeyRound className="w-3 h-3" />
          <span>Senha cadastrada</span>
          <CheckCircle2 className="w-3 h-3 text-green-500" />
          {certStatus.nomeArquivo && (
            <span className="text-slate-400">• {certStatus.nomeArquivo}</span>
          )}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              Senha do Certificado Digital
            </DialogTitle>
            <DialogDescription>
              Arquivo: <strong>{selectedFile?.name}</strong>. A senha será armazenada de forma
              criptografada e não poderá ser visualizada por nenhum administrador ou proprietário.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cert-senha">Senha do certificado</Label>
              <div className="relative">
                <Input
                  id="cert-senha"
                  type={showSenha ? "text" : "password"}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="Digite a senha do certificado"
                  className="pr-10"
                  autoComplete="off"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowSenha((v) => !v)}
                >
                  {showSenha ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cert-senha-confirm">Confirmar senha</Label>
              <Input
                id="cert-senha-confirm"
                type={showSenha ? "text" : "password"}
                value={senhaConfirm}
                onChange={(e) => setSenhaConfirm(e.target.value)}
                placeholder="Confirme a senha"
                autoComplete="off"
              />
              {senhaConfirm && senha !== senhaConfirm && (
                <p className="text-xs text-red-500">As senhas não conferem.</p>
              )}
            </div>

            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
              <span>
                Esta senha é <strong>altamente confidencial</strong>. Será criptografada e nenhum
                usuário — incluindo o proprietário da plataforma — terá acesso a ela. Guarde-a em
                local seguro.
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!senha || senha !== senhaConfirm || submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar e Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── ART Badge + Dialog for RT users ──────────────────────────────────────────

type RemontaArtShape = {
  id: string;
  rtId: string;
  numeroArt: string;
  descricao: string | null;
  valor: string | null;
  dataInicio: string;
  dataFim: string | null;
  status: string;
  criadoEm: string;
};

function ArtSection({
  user,
  canManage,
  session,
}: {
  user: UserProfile;
  canManage: boolean;
  session: { access_token: string } | null;
}) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    numeroArt: "",
    descricao: "",
    dataInicio: "",
    dataFim: "",
    valor: "",
  });

  const { data: arts = [], refetch } = useQuery<RemontaArtShape[]>({
    queryKey: [`remonta-arts-${user.id}`],
    queryFn: async () => {
      const res = await fetch(`/api/remonta/arts?rtId=${user.id}`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!session,
  });

  const activeArt = arts.find((art) => isCurrentArt(art));

  const createMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const res = await fetch("/api/remonta/arts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ ...data, rtId: user.id, status: "ativa" }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar ART");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "ART criada com sucesso." });
      refetch();
      setDialogOpen(false);
      setForm({ numeroArt: "", descricao: "", dataInicio: "", dataFim: "", valor: "" });
    },
    onError: (err: Error) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="mt-3">
      <button
        onClick={() => setDialogOpen(true)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
          activeArt
            ? "bg-green-100 text-green-700 hover:bg-green-200"
            : "bg-amber-100 text-amber-700 hover:bg-amber-200",
        )}
        title={canManage ? "Gerenciar ART" : "Ver ART"}
      >
        <FileText className="h-3 w-3" />
        {activeArt ? `ART Ativa — ${activeArt.numeroArt}` : "Sem ART"}
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>ART — {user.nome}</DialogTitle>
            <DialogDescription>
              Anotação de Responsabilidade Técnica do Responsável Técnico.
            </DialogDescription>
          </DialogHeader>

          {arts.length > 0 && (
            <div className="space-y-2 mb-4">
              <p className="text-sm font-medium text-muted-foreground">Histórico de ARTs</p>
              {arts.map((a) => (
                <div
                  key={a.id}
                  className="rounded border p-2 text-sm flex justify-between items-center"
                >
                  <span className="font-mono">{a.numeroArt}</span>
                  <span
                    className={cn(
                      "text-xs rounded-full px-2 py-0.5",
                      a.status === "ativa"
                        ? "bg-green-100 text-green-700"
                        : "bg-slate-100 text-slate-500",
                    )}
                  >
                    {a.status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {canManage && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate(form);
              }}
              className="space-y-3"
            >
              <p className="text-sm font-medium">Adicionar nova ART</p>
              <div className="space-y-1">
                <Label htmlFor={`art-numero-${user.id}`}>Número ART *</Label>
                <Input
                  id={`art-numero-${user.id}`}
                  value={form.numeroArt}
                  onChange={(e) => setForm((f) => ({ ...f, numeroArt: e.target.value }))}
                  placeholder="Ex: 1234567890"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor={`art-inicio-${user.id}`}>Data Início *</Label>
                  <Input
                    id={`art-inicio-${user.id}`}
                    type="date"
                    value={form.dataInicio}
                    onChange={(e) => setForm((f) => ({ ...f, dataInicio: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`art-fim-${user.id}`}>Data Fim</Label>
                  <Input
                    id={`art-fim-${user.id}`}
                    type="date"
                    value={form.dataFim}
                    onChange={(e) => setForm((f) => ({ ...f, dataFim: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor={`art-valor-${user.id}`}>Valor (R$)</Label>
                <Input
                  id={`art-valor-${user.id}`}
                  type="number"
                  step="0.01"
                  value={form.valor}
                  onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`art-desc-${user.id}`}>Descrição</Label>
                <Input
                  id={`art-desc-${user.id}`}
                  value={form.descricao}
                  onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                  placeholder="Descrição da ART (opcional)"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  disabled={createMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Salvar ART
                </Button>
              </DialogFooter>
            </form>
          )}

          {!canManage && arts.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhuma ART cadastrada.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function Usuarios() {
  const { profile, session } = useAuth();
  const isOwner = profile?.roles?.includes("owner") ?? false;
  const isAdmin = profile?.roles?.includes("admin") ?? false;
  const canCreate = isOwner || isAdmin;

  // Owner vê todos; admin vê sua empresa + parceiros (via vistoriadoraId); demais só a sua
  const { data: usuarios, isLoading } = useUsuarios(
    !isOwner && !isAdmin ? profile?.empresaId : undefined,
    true,
    !isOwner && isAdmin ? profile?.empresaId : undefined,
  );
  const { data: empresas } = useEmpresas();

  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | undefined>();
  const [userParaResetarSenha, setUserParaResetarSenha] = useState<
    UserProfile | undefined
  >();
  const [openResetSenha, setOpenResetSenha] = useState(false);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  const usuariosFiltrados = useMemo(() => {
    if (!usuarios) return undefined;
    let list = usuarios;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.nome.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q),
      );
    }
    if (filterRole !== "all") {
      list = list.filter((u) => u.roles.includes(filterRole));
    }
    return list;
  }, [usuarios, search, filterRole]);

  const empresasGerentiadasIds = new Set(empresas?.map((e) => e.id) ?? []);

  function canEdit(user: UserProfile) {
    if (isOwner) return true;
    if (isAdmin && empresasGerentiadasIds.has(user.empresaId)) return true;
    return false;
  }

  const canViewArt = (target: UserProfile) =>
    isOwner ||
    profile?.id === target.id ||
    (isAdmin && empresasGerentiadasIds.has(target.empresaId));

  // Agrupar usuários por empresa (usa lista filtrada)
  const usuariosPorEmpresa = usuariosFiltrados
    ? Object.groupBy(usuariosFiltrados, (u) => u.empresaId)
    : {};

  // Ordenar usuários pela role de maior hierarquia: owner → admin → rt → tv → parceiro_negocios
  const roleOrder: Record<string, number> = {
    owner: 0,
    admin: 1,
    rt: 2,
    tv: 3,
    parceiro_negocios: 4,
  };
  const sortUsersByRole = (users: typeof usuarios) =>
    users
      ? [...users].sort((a, b) => {
          const rankA = Math.min(...a.roles.map((r) => roleOrder[r] ?? 99));
          const rankB = Math.min(...b.roles.map((r) => roleOrder[r] ?? 99));
          return rankA - rankB;
        })
      : [];

  // Ordenar empresas por tipo: OWNER → VISTORIADORA → PARCEIRO_DE_NEGOCIOS
  // Deriva a lista a partir dos usuários para garantir que todas as empresas aparecem,
  // mesmo que useEmpresas não retorne as empresas parceiras para admins de vistoriadora.
  const tipoOrder: Record<string, number> = {
    OWNER: 0,
    VISTORIADORA: 1,
    PARCEIRO_DE_NEGOCIOS: 2,
  };
  const empresasOrdenadas = Object.keys(usuariosPorEmpresa)
    .map(
      (empresaId) =>
        empresas?.find((e) => e.id === empresaId) ?? {
          id: empresaId,
          tipo: "PARCEIRO_DE_NEGOCIOS" as const,
        },
    )
    .filter((empresa) => isOwner || empresa.tipo !== "OWNER")
    .sort((a, b) => (tipoOrder[a.tipo] ?? 99) - (tipoOrder[b.tipo] ?? 99));

  return (
    <Shell>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1
            className="text-3xl font-bold font-display text-slate-900"
            data-testid="text-page-title"
          >
            Usuários
          </h1>
          <p className="text-muted-foreground mt-1">Equipe da empresa</p>
        </div>
        {canCreate && !editingUser && (
          <Button
            onClick={() => setShowForm((v) => !v)}
            variant={showForm ? "outline" : "default"}
          >
            {showForm ? (
              <>
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                Novo usuário
              </>
            )}
          </Button>
        )}
      </div>

      {canCreate && showForm && !editingUser && (
        <div className="mb-8 p-6 rounded-xl border bg-white shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Novo usuário</h2>
          <CreateUsuarioForm
            onClose={() => setShowForm(false)}
            isOwner={isOwner}
            isAdmin={isAdmin}
            fixedEmpresaId={isOwner ? undefined : profile?.empresaId}
          />
        </div>
      )}

      {editingUser && (
        <div className="mb-8 p-6 rounded-xl border bg-white shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Editar usuário</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEditingUser(undefined)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <EditUsuarioForm
            user={editingUser}
            onClose={() => setEditingUser(undefined)}
            isOwner={isOwner}
          />
        </div>
      )}

      {editingUser?.roles.includes('rt') && canEdit(editingUser) && <div className="mb-8"><CertificadoDigital key={editingUser.id} userId={editingUser.id} nome={editingUser.nome} hasCpf={!!editingUser.cpf?.trim()} /></div>}

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-[160px] h-9">
            <SelectValue placeholder="Todos os perfis" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os perfis</SelectItem>
            {Object.entries(ROLE_LABELS).map(([role, label]) => (
              <SelectItem key={role} value={role}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(search || filterRole !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch("");
              setFilterRole("all");
            }}
          >
            <X className="w-4 h-4 mr-1" />
            Limpar
          </Button>
        )}
      </div>

      <div className="space-y-8">
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <Skeleton key={i} className="h-48 rounded-xl" />
              ))}
          </div>
        ) : usuariosFiltrados && usuariosFiltrados.length > 0 ? (
          empresasOrdenadas.map((empresa) => {
            const users = usuariosPorEmpresa[empresa.id];
            return (
              <div key={empresa.id} className="space-y-4">
                <div className="border-b border-slate-200 pb-2">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {(empresa as any).nomeFantasia ||
                      (empresa as any).razaoSocial ||
                      empresa.id}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {users?.length || 0} usuário{users?.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {sortUsersByRole(users)?.map((user) => (
                    <Card
                      key={user.id}
                      className="min-w-0 border-none shadow-md hover:shadow-lg transition-shadow"
                      data-testid={`card-usuario-${user.id}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="w-10 h-10 shrink-0 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600">
                              {user.nome.substring(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <CardTitle className="break-words text-base">
                                {user.nome}
                              </CardTitle>
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {user.roles.map((role) => (
                                  <Badge
                                    key={role}
                                    variant="secondary"
                                    className={cn(
                                      "text-xs",
                                      ROLE_BADGE_COLORS[role] ||
                                        "bg-slate-100 text-slate-700",
                                    )}
                                  >
                                    {ROLE_LABELS[role] || role.toUpperCase()}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            {!user.ativo && (
                              <Badge variant="destructive" className="text-xs">
                                Inativo
                              </Badge>
                            )}
                            {canEdit(user) && (
                              <>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="w-8 h-8 text-slate-400 hover:text-slate-700"
                                  title="Editar usuário"
                                  onClick={() => {
                                    setShowForm(false);
                                    setEditingUser(user);
                                  }}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="w-8 h-8 text-slate-400 hover:text-slate-700"
                                  title="Redefinir senha"
                                  onClick={() => {
                                    setUserParaResetarSenha(user);
                                    setOpenResetSenha(true);
                                  }}
                                >
                                  <Lock className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-500">
                          <Mail className="w-3.5 h-3.5 shrink-0" />
                          <span className="min-w-0 truncate">{user.email}</span>
                        </div>
                        {user.telefone && (
                          <div className="flex items-center gap-2 text-slate-500">
                            <Phone className="w-3.5 h-3.5" />
                            <span>{user.telefone}</span>
                          </div>
                        )}
                        {user.cpf && (
                          <p className="text-slate-500">
                            CPF: {formatCpf(user.cpf)}
                          </p>
                        )}
                        {user.crea && (
                          <p className="text-slate-500">CREA: {user.crea}</p>
                        )}
                        {user.rebaixadoEm && (
                          <p className="text-xs text-red-500 mt-1">
                            Rebaixado em:{" "}
                            {new Date(user.rebaixadoEm).toLocaleDateString(
                              "pt-BR",
                            )}
                          </p>
                        )}
                        {/* CertificadoDigitalCard removido — feature não disponível nesta versão */}
                        {user.roles.includes("rt") && (
                          <ArtSection
                            user={user}
                            canManage={canViewArt(user)}
                            session={session}
                          />
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            Nenhum usuário encontrado
          </div>
        )}
      </div>

      {userParaResetarSenha && (
        <AlterarSenhaDialog
          open={openResetSenha}
          onOpenChange={setOpenResetSenha}
          usuarioId={userParaResetarSenha.id}
          usuarioNome={userParaResetarSenha.nome}
          isOwnPassword={false}
          onSuccess={() => {
            setUserParaResetarSenha(undefined);
            setOpenResetSenha(false);
          }}
        />
      )}
    </Shell>
  );
}
